import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { formatNpr } from '../../lib/currency';
import { getOptimizedImageUrl } from '../../lib/imageUrl';
import { getProductDisplayId } from '../../lib/productDisplayId';

function CartDrawer() {
  const {
    cart,
    cartError,
    cartIsLoading,
    clearAll,
    closeCart,
    isCartOpen,
    removeItem,
    updateItem,
  } = useCart();

  useEffect(() => {
    if (!isCartOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        closeCart();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeCart, isCartOpen]);

  return (
    <div className={`cart-drawer ${isCartOpen ? 'is-open' : ''}`} aria-hidden={!isCartOpen}>
      <button
        type="button"
        className="cart-drawer__backdrop"
        aria-label="Close cart drawer"
        onClick={closeCart}
      />
      <aside
        className="cart-drawer__panel"
        aria-label="Shopping cart"
        aria-modal="true"
        role="dialog"
      >
        <header className="cart-drawer__header">
          <div>
            <p className="cart-drawer__eyebrow">Shopping Cart</p>
            <h2 className="cart-drawer__title">Your bag</h2>
          </div>
          <button
            type="button"
            className="cart-drawer__close"
            aria-label="Close cart"
            onClick={closeCart}
          >
            ×
          </button>
        </header>

        <div className="cart-drawer__content">
          {cartIsLoading ? <p className="products-state">Loading cart...</p> : null}
          {cartError ? <p className="products-state products-state--error">{cartError}</p> : null}
          {!cartIsLoading && cart.items.length === 0 ? (
            <div className="cart-drawer__empty">
              <p>Your cart is empty.</p>
              <Link className="product-card__link" to="/products" onClick={closeCart}>
                Continue Shopping
              </Link>
            </div>
          ) : null}

          {cart.items.length > 0 ? (
            <div className="cart-drawer__items">
              {cart.items.map((item) => (
                <article key={item.id} className="cart-drawer__item">
                  <img
                    className="cart-drawer__item-image"
                    src={getOptimizedImageUrl(item.product.image, 200)}
                    alt={item.product.name}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="cart-drawer__item-copy">
                    <h3>{item.product.name}</h3>
                    <p>{item.product.category}</p>
                    <p className="cart-drawer__item-id">
                      Product ID: {getProductDisplayId(item.product.id)}
                    </p>
                    {item.product.featured ? (
                      <div className="cart-drawer__offer">
                        <span className="cart-drawer__offer-badge">50% OFF</span>
                        <span className="cart-drawer__offer-text">Today offer product</span>
                      </div>
                    ) : null}
                    {item.product.featured ? (
                      <div className="cart-drawer__offer-price">
                        <span className="cart-drawer__offer-price-old">
                          {formatNpr(item.originalUnitPrice)}
                        </span>
                        <strong>{formatNpr(item.unitPrice)}</strong>
                      </div>
                    ) : null}
                    <div className="cart-quantity cart-quantity--drawer">
                      <button
                        type="button"
                        aria-label={`Decrease quantity for ${item.product.name}`}
                        onClick={() => {
                          void updateItem(item.id, item.quantity - 1).catch(() => {});
                        }}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        aria-label={`Increase quantity for ${item.product.name}`}
                        onClick={() => {
                          void updateItem(item.id, item.quantity + 1).catch(() => {});
                        }}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="cart-drawer__remove"
                      onClick={() => {
                        void removeItem(item.id).catch(() => {});
                      }}
                    >
                      Remove
                    </button>
                  </div>
                  <strong className="cart-drawer__item-price">
                    {formatNpr(item.lineTotal)}
                  </strong>
                </article>
              ))}
            </div>
          ) : null}
        </div>

        <footer className="cart-drawer__footer">
          <div className="cart-drawer__summary">
            <div>
              <strong>Total</strong>
              <span>{cart.itemCount} item{cart.itemCount === 1 ? '' : 's'}</span>
            </div>
            <strong>{formatNpr(cart.subtotal)}</strong>
          </div>
          <p className="cart-drawer__shipping">Shipping cost will appear on checkout.</p>
          {cart.items.length > 0 ? (
            <Link className="cart-drawer__checkout" to="/checkout" onClick={closeCart}>
              Checkout
            </Link>
          ) : (
            <Link className="cart-drawer__checkout" to="/products" onClick={closeCart}>
              Continue Shopping
            </Link>
          )}
          <div className="cart-drawer__footer-actions">
            <Link className="cart-drawer__view-cart" to="/cart" onClick={closeCart}>
              Open full cart
            </Link>
            <button
              type="button"
              className="cart-drawer__clear"
              onClick={() => {
                void clearAll().catch(() => {});
              }}
            >
              Clear cart
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

export default CartDrawer;
